import { create } from "zustand";

export type AgentActivityEntry = {
  id: number;
  label: string;
  observedAt: number;
};

/**
 * Scope key partitioning agent activity by the content the agent is working
 * on (e.g. "course-123"). The module builder polls builder actions for the
 * module AND every child course/skill at once, and module generation runs one
 * agent per child concurrently — an unpartitioned store made every embedded
 * course share one "Agent is working" flag, one activity label, and one
 * working-screen pointer, so course B's header showed course A's activity.
 */
export type AgentActivityScope = string;

export const courseAgentScope = (
  courseId: number | null | undefined,
): AgentActivityScope | undefined =>
  courseId != null ? `course-${courseId}` : undefined;

export const agentScopeForContent = (contentIds: {
  courseId?: number | string | null;
  skillId?: number | string | null;
  moduleId?: number | string | null;
  trainingResourceId?: number | string | null;
}): AgentActivityScope | undefined => {
  if (contentIds.courseId != null) return `course-${contentIds.courseId}`;
  if (contentIds.skillId != null) return `skill-${contentIds.skillId}`;
  if (contentIds.trainingResourceId != null)
    return `resource-${contentIds.trainingResourceId}`;
  if (contentIds.moduleId != null) return `module-${contentIds.moduleId}`;
  return undefined;
};

export const AGENT_RECENT_CHANGE_WINDOW_MS = 4000;

// Safety cap: a screen stops showing as "being worked on" at most this long
// after the last change, in case a typewriter-completion signal never arrives
// (e.g. the title screen, which isn't a typewriter editor).
export const AGENT_WORKING_SCREEN_MAX_MS = 60000;

const MAX_ENTRIES = 25;

const EMPTY_ENTRIES: AgentActivityEntry[] = [];

type AgentActivityState = {
  entriesByScope: Record<AgentActivityScope, AgentActivityEntry[]>;
  workingByScope: Record<AgentActivityScope, boolean>;
  // screenId (prompt uuid — globally unique) -> observedAt of the last agent
  // write. A map rather than a single "current screen": with N course agents
  // generating concurrently, a single pointer was overwritten by whichever
  // course acted last, flickering the visible course's typewriter state.
  workingScreens: Record<string, number>;
  // Screens whose typewriter has finished — they stay "done" for the rest of the
  // generation so the indicator never reappears on an already-built slide.
  completedScreenIds: Set<string>;
  recordActivity: (
    scope: AgentActivityScope,
    entries: AgentActivityEntry[],
  ) => void;
  clearActivity: () => void;
  setWorking: (scope: AgentActivityScope, working: boolean | null) => void;
  setWorkingScreen: (screenId: string, observedAt: number) => void;
  completeWorkingScreen: (screenId: string) => void;
};

export const useAgentActivityStore = create<AgentActivityState>((set) => ({
  entriesByScope: {},
  workingByScope: {},
  workingScreens: {},
  completedScreenIds: new Set(),
  recordActivity: (scope, entries) =>
    set((state) => {
      if (entries.length === 0) return state;
      const prev = state.entriesByScope[scope] ?? EMPTY_ENTRIES;
      return {
        entriesByScope: {
          ...state.entriesByScope,
          [scope]: [...prev, ...entries].slice(-MAX_ENTRIES),
        },
      };
    }),
  clearActivity: () =>
    set({
      entriesByScope: {},
      workingScreens: {},
      completedScreenIds: new Set(),
    }),
  setWorking: (scope, working) =>
    set((state) => {
      if (working === null) {
        if (!(scope in state.workingByScope)) return state;
        const next = { ...state.workingByScope };
        delete next[scope];
        return { workingByScope: next };
      }
      if (state.workingByScope[scope] === working) return state;
      return { workingByScope: { ...state.workingByScope, [scope]: working } };
    }),
  setWorkingScreen: (screenId, observedAt) =>
    set((state) => {
      const workingScreens = {
        ...state.workingScreens,
        [screenId]: observedAt,
      };
      // Re-working a screen clears its "done" status so it can show again.
      if (!state.completedScreenIds.has(screenId)) {
        return { workingScreens };
      }
      const next = new Set(state.completedScreenIds);
      next.delete(screenId);
      return { workingScreens, completedScreenIds: next };
    }),
  completeWorkingScreen: (screenId) =>
    set((state) => {
      if (state.completedScreenIds.has(screenId)) return {};
      return {
        completedScreenIds: new Set(state.completedScreenIds).add(screenId),
      };
    }),
}));

export const selectScopeEntries =
  (scope: AgentActivityScope | undefined) =>
  (state: AgentActivityState): AgentActivityEntry[] =>
    scope ? state.entriesByScope[scope] ?? EMPTY_ENTRIES : EMPTY_ENTRIES;

export const selectScopeWorking =
  (scope: AgentActivityScope | undefined) =>
  (state: AgentActivityState): boolean | null =>
    scope != null ? state.workingByScope[scope] ?? null : null;
